/**
 * API Client for TimePoint Django Backend
 * Generated types should be placed in schema.d.ts via:
 * npm run generate:api
 */

import { signOut } from 'next-auth/react';

// Determine API base URL based on environment.
//
// H5: Client-side calls are routed through the Next.js proxy at /api/proxy
// so the Django JWT never leaves the server. The proxy reads the token from
// the encrypted httpOnly JWE cookie and forwards it to Django.
//
// Server-side calls (RSC, Route Handlers) still hit Django directly via the
// internal Docker network — they never go through the browser.
export function getApiBaseUrl(): string {
  // Server-side rendering: use internal Docker network directly
  if (typeof window === 'undefined') {
    return process.env.API_URL || 'http://backend:8000';
  }

  // Client-side: route through the server-side proxy (H5)
  // /api/proxy/v1/... → proxy → http://backend:8000/api/v1/...
  return '/api/proxy';
}

const API_BASE_URL = getApiBaseUrl();

type FallbackMessageKey = 'sessionExpired' | 'requestFailed';

/** English `apiErrors.*` copy: server side, English UI, or if the catalog chunk fails to load. */
const FALLBACK_MESSAGES_EN: Record<FallbackMessageKey, string> = {
  sessionExpired: 'Session expired',
  requestFailed: 'Request failed',
};

/**
 * Text for an error the backend sent no message for. Many call sites show
 * `ApiError.message` verbatim, so it follows the UI language: `<html lang>`
 * carries the next-intl locale, and the Finnish catalog is code-split — it is
 * only fetched when such an error actually happens.
 */
async function fallbackMessage(key: FallbackMessageKey): Promise<string> {
  if (typeof document === 'undefined' || document.documentElement.lang !== 'fi') {
    return FALLBACK_MESSAGES_EN[key];
  }
  try {
    const { default: fi } = await import('@/messages/fi.json');
    return fi.apiErrors[key];
  } catch {
    return FALLBACK_MESSAGES_EN[key];
  }
}

interface RequestOptions extends RequestInit {
  token?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * @deprecated H5: Token is now injected server-side by the /api/proxy route.
   * This method is a no-op and will be removed once all call sites are cleaned up.
   */
  setToken(_token: string | null) {
    // no-op — token is handled by the server-side proxy
  }

  private normalizeEndpoint(endpoint: string): string {
    // H5 proxy expects /api/proxy/v1/... (not /api/proxy/api/v1/...).
    // Keep legacy call sites working by stripping the leading /api when
    // the browser-side proxy base URL is in use.
    if (
      this.baseUrl === '/api/proxy' &&
      endpoint.startsWith('/api/') &&
      !endpoint.startsWith('/api/proxy/')
    ) {
      return endpoint.slice(4);
    }
    return endpoint;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { token: _legacyToken, ...fetchOptions } = options;
    const headers = new Headers(fetchOptions.headers);
    const normalizedEndpoint = this.normalizeEndpoint(endpoint);

    headers.set('Content-Type', 'application/json');
    // H5: No Authorization header set client-side — the /api/proxy route
    // reads the token from the httpOnly JWE cookie and adds it server-side.

    const response = await fetch(`${this.baseUrl}${normalizedEndpoint}`, {
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      // 401 from the proxy means the session has expired — sign out cleanly
      if (response.status === 401 && typeof window !== 'undefined') {
        signOut({ callbackUrl: '/login' });
        throw new ApiError(401, await fallbackMessage('sessionExpired'));
      }

      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.detail ||
          errorData.error ||
          errorData.message ||
          (await fallbackMessage('requestFailed')),
        errorData
      );
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // Auth endpoints
  async login(email: string, password: string) {
    return this.request<{
      access: string;
      refresh: string;
      needs_onboarding?: boolean;
      user?: { id: string; email: string };
    }>('/api/v1/auth/token/', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async refreshToken(refresh: string) {
    return this.request<{ access: string }>('/api/v1/auth/token/refresh/', {
      method: 'POST',
      body: JSON.stringify({ refresh }),
    });
  }

  // Generic CRUD methods
  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint: string, options?: RequestOptions): Promise<void> {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  async fetchRaw(endpoint: string, options?: RequestOptions): Promise<Response> {
    const { token: _legacyToken, ...fetchOptions } = options || {};
    const headers = new Headers(fetchOptions.headers);
    const normalizedEndpoint = this.normalizeEndpoint(endpoint);
    // H5: No Authorization header — proxy adds it server-side

    const response = await fetch(`${this.baseUrl}${normalizedEndpoint}`, {
      ...fetchOptions,
      headers,
      method: fetchOptions.method || 'GET',
    });

    if (!response.ok) {
      if (response.status === 401 && typeof window !== 'undefined') {
        signOut({ callbackUrl: '/login' });
        throw new ApiError(401, await fallbackMessage('sessionExpired'));
      }
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.detail || errorData.error || (await fallbackMessage('requestFailed')),
        errorData
      );
    }

    return response;
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Extract DRF field-level validation errors from an ApiError into a flat map.
 *
 * DRF returns field errors as:
 *   { "email": ["This field is required."], "password": ["Too short."] }
 * or non-field errors as:
 *   { "non_field_errors": ["Invalid credentials."] }
 * or a plain string error:
 *   { "error": "Something went wrong." }
 *
 * Returns a map of field name → first error message string.
 * Non-field / top-level errors are keyed as "__all__".
 *
 * @example
 * try {
 *   await apiClient.post('/api/v1/auth/register/', data);
 * } catch (err) {
 *   if (err instanceof ApiError) {
 *     const fieldErrors = extractFieldErrors(err);
 *     // { email: "This field is required.", __all__: "..." }
 *   }
 * }
 */
export function extractFieldErrors(err: ApiError): Record<string, string> {
  const result: Record<string, string> = {};
  const data = err.data;

  if (!data || typeof data !== 'object') {
    result['__all__'] = err.message;
    return result;
  }

  for (const [key, value] of Object.entries(data)) {
    // Skip top-level scalar keys already captured in err.message
    if (key === 'detail' || key === 'error' || key === 'message') {
      continue;
    }

    const targetKey = key === 'non_field_errors' ? '__all__' : key;

    if (Array.isArray(value) && value.length > 0) {
      result[targetKey] = String(value[0]);
    } else if (typeof value === 'string') {
      result[targetKey] = value;
    }
  }

  // If nothing was extracted, fall back to the top-level message
  if (Object.keys(result).length === 0) {
    result['__all__'] = err.message;
  }

  return result;
}

export const apiClient = new ApiClient(API_BASE_URL);
